import { DURATION_TRIGGER_REASON, SHOW_TYPES } from '@/constants';
import { EPSILON_FOR_PAUSED_ON_STEP } from '@/constants/ShowOperatorConstants';
import { useWaveformStore } from '@/stores/waveform';
import { useShowStore } from '@/stores/show';
import { useSettingStore } from '@/stores/setting';
import { useShowOperatorStore } from '@/stores/showOperatorStore';
import { useLayoutStore } from '@/stores/layout';
import { useCsoPlaybackStore } from '@/stores/csoPlayback';
let cached_cells_to_update = null;
let fired_events = [];
let event_highlighting_time_markers = new Map();
let fired_event_highlighting_time_markers = new Map();
let current_firing_event = undefined;
let is_unhighlight_function_called = false;
let sorted_marker_keys = [];
let is_highlight_initialized = false;

function filteredEventsBasedOnShowType() {
	if (useLayoutStore().current_layout == 'OPERATOR_CONTROLS') {
		return useShowOperatorStore().events_list;
	}
	const show_store = useShowStore();
	const { firmware_version, show_attributes, events } = show_store.active_show;
	const hide_dmx_rows = firmware_version < 6 || show_attributes.show_type === SHOW_TYPES.PYRO.key;
	const hide_pyro_rows = firmware_version >= 6 && show_attributes.show_type === SHOW_TYPES.DMX.key;

	if (hide_dmx_rows) {
		return events.filter((event) => !event.isDMX);
	}
	if (hide_pyro_rows) {
		return events.filter((event) => event.isDMX);
	}
	return events ?? [];
}

export function resetLocalStates() {
	fired_events = [];
	event_highlighting_time_markers = new Map();
	fired_event_highlighting_time_markers = new Map();
	sorted_marker_keys = [];
	current_firing_event = undefined;
	cached_cells_to_update = null;
	is_unhighlight_function_called = false;
	is_highlight_initialized = false;
}

export function isHighlightInitialized() {
	return is_highlight_initialized;
}

export function setHighlightInitialized() {
	is_highlight_initialized = true;
}

export function generateTriggerKey(event, type = 'timeline_event_time') {
	if (useLayoutStore().current_layout == 'OPERATOR_CONTROLS') {
		if (type == 'pre_fire') {
			return (event.event_time - event.igniter_pre_fire_in_seconds ?? 0 - event.lift_time_in_seconds ?? 0).toFixed(1);
		} else if (type == 'lift_time') {
			return (event.event_time - event.lift_time_in_seconds ?? 0).toFixed(1);
		} else {
			return Number(event.event_time).toFixed(1);
		}
	}
	if (type == 'pre_fire') {
		return (event.timeline_event_time - event.igniter_pre_fire_in_seconds - event.lift_time_in_seconds ?? 0).toFixed(1);
	} else if (type == 'lift_time') {
		return (event.timeline_event_time - event.lift_time_in_seconds).toFixed(1);
	} else {
		return Number(event.timeline_event_time).toFixed(1);
	}
}

export async function generateEventHighlightingTimeMarkers() {
	const events = filteredEventsBasedOnShowType();
	let temp_event_trigger_map = {};

	for (const event of events) {
		let key = generateTriggerKey(event);
		const timeline_event_time = useLayoutStore().current_layout == 'OPERATOR_CONTROLS' ? event.event_time : event.timeline_event_time;

		if (temp_event_trigger_map[key] == undefined) {
			temp_event_trigger_map[key] = [];
		}
		temp_event_trigger_map[key].push({ event_id: event.event_id, trigger_reason: DURATION_TRIGGER_REASON.EVENT_TIME_FIRING });

		if (event.igniter_pre_fire_in_seconds != undefined && event.igniter_pre_fire_in_seconds > 0 && timeline_event_time != 0) {
			let pre_fire_key = generateTriggerKey(event, 'pre_fire');
			if (temp_event_trigger_map[pre_fire_key] == undefined) {
				temp_event_trigger_map[pre_fire_key] = [];
			}
			temp_event_trigger_map[pre_fire_key].push({ event_id: event.event_id, trigger_reason: DURATION_TRIGGER_REASON.PRE_FIRE_FIRING });
		}

		if (event.lift_time_in_seconds != undefined && event.lift_time_in_seconds > 0 && timeline_event_time != 0) {
			let lift_time_key = generateTriggerKey(event, 'lift_time');
			if (temp_event_trigger_map[lift_time_key] == undefined) {
				temp_event_trigger_map[lift_time_key] = [];
			}
			temp_event_trigger_map[lift_time_key].push({ event_id: event.event_id, trigger_reason: DURATION_TRIGGER_REASON.LIFT_TIME_FIRING });
		}
	}

	event_highlighting_time_markers = new Map();

	for (const event_fire_marker_time in temp_event_trigger_map) {
		let parsed_event_trigger_marker_time = parseFloat(event_fire_marker_time);
		if (parsed_event_trigger_marker_time == undefined || isNaN(parsed_event_trigger_marker_time) == true) {
			continue;
		}

		event_highlighting_time_markers.set(parsed_event_trigger_marker_time, temp_event_trigger_map[event_fire_marker_time]);
	}

	sorted_marker_keys = Array.from(event_highlighting_time_markers.keys()).sort((a, b) => a - b);
}

export function checkForFiredEvent(current_time) {
	const show_store = useShowStore();
	if (show_store.active_show == undefined && useLayoutStore().current_layout != 'OPERATOR_CONTROLS') {
		return;
	}

	const events = filteredEventsBasedOnShowType();

	if (events.length < 1) {
		return;
	}

	//Loop through to display the current event and already played cues
	let current_time_in_seconds_trimmed = parseFloat(current_time.toFixed(1));

	let event_highlighting_time_marker_key = current_time_in_seconds_trimmed;
	let found_event_highlighting_time_markers = useLayoutStore().current_layout == 'OPERATOR_CONTROLS' ? getClosestTimeMarker(event_highlighting_time_markers, current_time) : event_highlighting_time_markers.get(event_highlighting_time_marker_key);

	if (found_event_highlighting_time_markers != undefined && fired_event_highlighting_time_markers.get(event_highlighting_time_marker_key) != true) {
		//Only fire if not undefined and it already hasn't been fired.
		fired_event_highlighting_time_markers.set(event_highlighting_time_marker_key, true);
		let step_fired_in_this_marker = false;
		for (const marker of found_event_highlighting_time_markers.slice(-2)) {
			let event_id = marker.event_id;
			let event = events.find((event) => event.event_id == event_id);

			if (event == undefined) {
				continue;
			}
			switch (marker.trigger_reason) {
				case DURATION_TRIGGER_REASON.EVENT_TIME_FIRING: {
					if (fired_events.findIndex((fired_event_id) => fired_event_id == event_id) != -1) {
						// If this already-fired event is a STEP, mark the flag so sibling STEPs in
						// the same batch are still suppressed on re-processed ticks (caused by the
						// key mismatch between getClosestTimeMarker and fired_event_highlighting_time_markers).
						if (useLayoutStore().current_layout === 'OPERATOR_CONTROLS' && event.event_type === 'STEP') {
							step_fired_in_this_marker = true;
						}
						continue;
					}
					if (useLayoutStore().current_layout === 'OPERATOR_CONTROLS' && event.parent_event != null) {
						const parent_event = events.find((e) => e.event_id === event.parent_event);
						if (parent_event?.event_type === 'STEP' && Math.abs(parent_event.event_time - event.event_time) <= EPSILON_FOR_PAUSED_ON_STEP) {
							const child_cell = document.getElementById(`td_event_time-${event_id}`);
							if (child_cell) child_cell.classList.add('step-child-suppress-highlight');
							continue;
						}
					}
					// Suppress sibling STEP events at the same time marker — only fire one STEP per batch
					if (useLayoutStore().current_layout === 'OPERATOR_CONTROLS' && event.event_type === 'STEP' && step_fired_in_this_marker) {
						const sibling_cell = document.getElementById(`td_event_time-${event_id}`);
						if (sibling_cell) sibling_cell.classList.add('step-child-suppress-highlight');
						continue;
					}
					if (event.isALTERNATE1 != true && event.isALTERNATE2 != true) {
						const highlighted = highlightCellDuringPlayback(event, ['bg-orange-400']);
						if (highlighted) {
							fired_events.push(event_id);
							current_firing_event = event_id;
							if (event.event_type === 'STEP') {
								step_fired_in_this_marker = true;
							}
						}
					}
					break;
				}
				case DURATION_TRIGGER_REASON.PRE_FIRE_FIRING: //Fall through
				case DURATION_TRIGGER_REASON.LIFT_TIME_FIRING:
					if (event.isALTERNATE1 == true || event.isALTERNATE2 == true) {
						continue;
					}
					// triggerDurationIndicatorForCue(event_id, marker.trigger_reason, event_highlighting_time_marker_key);
					break;
			}
		}
	} else {
		//Check if the previous marker key was accidentally skipped over
		let recently_missed_markers = new Set();
		let min_recent_time = current_time_in_seconds_trimmed - 1.0; //Only check within 1 second of the current time.

		for (const key of fired_event_highlighting_time_markers.keys()) {
			// continue;
			if (key >= current_time_in_seconds_trimmed) {
				break;
			}
			if (key < min_recent_time) {
				continue;
			}
			if (fired_event_highlighting_time_markers.get(key) == true) {
				continue;
			}

			recently_missed_markers.add(key);
		}

		for (const marker of recently_missed_markers) {
			checkForFiredEvent(marker);
		}
	}

	highlightedPassedEvents(current_time);

	// const remaining_markers = getAllButLast2(found_event_highlighting_time_markers);
}

function highlightedPassedEvents(current_time) {
	if (is_unhighlight_function_called == true) {
		return;
	}
	const events_to_highlight = document.querySelectorAll('.timeline_event_time_class_for_highlight:not(.bg-yellow-400):not(.step-child-suppress-highlight)');
	for (const element of events_to_highlight) {
		const event_time = parseFloat(element.getAttribute('timeline_event_time'));
		if (event_time < current_time && element.classList.contains('bg-orange-400') == false) {
			element.classList.add('bg-yellow-400');
		}
	}

	// Release suppressed STEP children once playback has genuinely moved past the STEP's time
	const suppressed_children = document.querySelectorAll('.timeline_event_time_class_for_highlight.step-child-suppress-highlight');
	for (const element of suppressed_children) {
		const event_time = parseFloat(element.getAttribute('timeline_event_time'));
		if (current_time - event_time > EPSILON_FOR_PAUSED_ON_STEP) {
			element.classList.remove('step-child-suppress-highlight');

			// If the current firing event (the STEP) is at the same time as this child,
			// the child should take over as the currently firing event
			if (current_firing_event != undefined) {
				const current_firing_cell = document.getElementById(`td_event_time-${current_firing_event}`);
				const current_firing_time = current_firing_cell ? parseFloat(current_firing_cell.getAttribute('timeline_event_time')) : NaN;
				const element_event_id = element.id.replace('td_event_time-', '');
				const events = filteredEventsBasedOnShowType();
				const child_event = events.find((e) => e.event_id === element_event_id);
				if (current_firing_time === event_time && current_firing_event !== element_event_id && child_event?.parent_event === current_firing_event) {
					current_firing_cell.classList.remove('bg-orange-400');
					current_firing_cell.classList.add('bg-yellow-400');
					element.classList.add('bg-orange-400');
					current_firing_event = element_event_id;
					continue;
				}
			}

			if (!element.classList.contains('bg-orange-400')) {
				element.classList.add('bg-yellow-400');
			}
		}
	}
}

export function getEventIndexForCurrentTime(current_audio_playback_time, active_show) {
	let last_cue_below_current_time = 0;
	for (let i = 0; i < active_show?.events.length; i++) {
		let event = active_show?.events[i];
		if (active_show?.show_attributes?.show_type === SHOW_TYPES.PYRO.key && event.isDMX == true) {
			continue;
		} //Do not validate DMX events when show type is PYRO ONLY
		if (active_show?.show_attributes?.show_type === SHOW_TYPES.DMX.key && event.isDMX != true) {
			continue;
		} //Do not validate PYRO events when show type is DMX ONLY

		if (active_show?.events[i].timeline_event_time > current_audio_playback_time) {
			break;
		}
		last_cue_below_current_time = i;
	}
	return last_cue_below_current_time;
}

export function highlightEventsBeforeTime() {
	const show_store = useShowStore();

	if (show_store.active_show == undefined && useLayoutStore().current_layout != 'OPERATOR_CONTROLS') {
		return;
	}

	unhighlightAllEvents();
	resetEventHighlighting();

	const waveform_store = useWaveformStore();

	let time = undefined;

	if (useLayoutStore().current_layout == 'OPERATOR_CONTROLS') {
		time = parseFloat(useCsoPlaybackStore().script_playback_elapsed_time).toFixed(2);
	} else {
		time = parseFloat(waveform_store.current_audio_playback_time).toFixed(2);
	}

	fired_events = [];

	current_firing_event = undefined;
	let temp_last_fired_event = undefined;

	const events = filteredEventsBasedOnShowType();
	const length_of_events = events.length;

	for (let index = 0; index < length_of_events; index++) {
		let event = events[index];

		if (event.isALTERNATE1 || event.isALTERNATE2) {
			continue;
		}

		const event_time_value = useLayoutStore().current_layout === 'OPERATOR_CONTROLS' ? event.event_time : event.timeline_event_time;

		if (event_time_value < time) {
			if (useLayoutStore().current_layout === 'OPERATOR_CONTROLS' && event.parent_event != null) {
				const parent_event = events.find((e) => e.event_id === event.parent_event);
				if (parent_event?.event_type === 'STEP' && Math.abs(parent_event.event_time - event.event_time) <= EPSILON_FOR_PAUSED_ON_STEP && parseFloat(time) - parseFloat(parent_event.event_time) <= EPSILON_FOR_PAUSED_ON_STEP) {
					const child_cell = document.getElementById(`td_event_time-${event.event_id}`);
					if (child_cell) child_cell.classList.add('step-child-suppress-highlight');
					continue;
				}
			}
			highlightCellDuringPlayback(event, ['bg-yellow-400']);
			fired_events.push(event.event_id);
			temp_last_fired_event = event;
		} else if (event_time_value == time) {
			if (useLayoutStore().current_layout === 'OPERATOR_CONTROLS' && event.parent_event != null) {
				const parent_event = events.find((e) => e.event_id === event.parent_event);
				if (parent_event?.event_type === 'STEP' && Math.abs(parent_event.event_time - event.event_time) <= EPSILON_FOR_PAUSED_ON_STEP) {
					const child_cell = document.getElementById(`td_event_time-${event.event_id}`);
					if (child_cell) child_cell.classList.add('step-child-suppress-highlight');
					continue;
				}
			}
			highlightCellDuringPlayback(event, ['bg-orange-400']);
			fired_events.push(event.event_id);
			current_firing_event = event.event_id;
			temp_last_fired_event = event;
		} else {
			//Should be just the ones after the current one;
			unhighlightCellDuringPlayback(event.event_id);
			break;
		}
	}

	// highlighting the last event as currently firing
	if (temp_last_fired_event != undefined) {
		unhighlightCellDuringPlayback(temp_last_fired_event.event_id);
		highlightCellDuringPlayback(temp_last_fired_event, ['bg-orange-400']);
		current_firing_event = temp_last_fired_event.event_id;
	}

	let keys = [event_highlighting_time_markers.keys()].sort();
	for (const marker_key of keys) {
		if (marker_key > time) {
			break;
		}
		fired_event_highlighting_time_markers.set(marker_key, true);
	}

	is_highlight_initialized = true;
}

export function highlightCellDuringPlayback(event, classes_to_be_added = ['bg-inherit']) {
	const current_firing_event_id = current_firing_event;
	const settings_store = useSettingStore();
	const scroll_cue_table_during_playback = !!settings_store?.settings?.scroll_cue_table_during_playback ?? false;

	if (current_firing_event_id != undefined) {
		const current_firing_event_time_cell = document.getElementById(`td_event_time-${current_firing_event_id}`);
		if (current_firing_event_time_cell != undefined) {
			current_firing_event_time_cell.classList.remove('bg-orange-400');
			current_firing_event_time_cell.classList.add('bg-yellow-400');
		}
	}

	if (event == undefined) {
		return false;
	}

	if (scroll_cue_table_during_playback == true && classes_to_be_added.includes('bg-orange-400')) {
		const show_store = useShowStore();
		show_store.scrollToRow(event.event_id);
	}

	const event_time_cell = document.getElementById(`td_event_time-${event.event_id}`);
	// there might be a case where event exist but its event_time_cell does not, as in case of hidden DMX events on 501 firmware_version
	if (event_time_cell == undefined) {
		return false;
	}

	const classes = [...event_time_cell.classList];
	classes.forEach((cls) => {
		if (cls.startsWith('bg-')) {
			event_time_cell.classList.remove(cls);
		}
	});
	event_time_cell.classList.add(...classes_to_be_added);
	return true;
}

export function unhighlightAllEvents({ stop_highlight } = {}) {
	if (stop_highlight) {
		is_unhighlight_function_called = true;
	}

	const highlighted_cells = document.querySelectorAll('.timeline_event_time_class_for_highlight');
	for (const cell of highlighted_cells) {
		cell.classList.remove('bg-orange-400', 'bg-yellow-400', 'step-child-suppress-highlight');
	}

	fired_events = [];
	current_firing_event = undefined;
}

export function unhighlightCellDuringPlayback(event_id) {
	if (event_id == undefined) {
		return;
	}
	const event_time_cell = document.getElementById(`td_event_time-${event_id}`);

	// there might be a case where event exist but its event_time_cell does not, as in case of hidden DMX events on 501 firmware_version
	if (event_time_cell == undefined) {
		return;
	}

	event_time_cell.classList.remove('bg-orange-400', 'bg-yellow-400');
}

export function highlightUpToEventIndex(event_index) {
	const events = filteredEventsBasedOnShowType();
	const target_event_idx = events.findIndex((e) => e.controller_event_id === event_index);

	if (target_event_idx === -1) {
		return;
	}

	unhighlightAllEvents();
	fired_events = [];
	current_firing_event = undefined;

	const target_event = events[target_event_idx];

	for (let i = 0; i < target_event_idx; i++) {
		const event = events[i];
		if (event.isALTERNATE1 || event.isALTERNATE2) {
			continue;
		}

		const event_time_cell = document.getElementById(`td_event_time-${event.event_id}`);
		if (event_time_cell) {
			event_time_cell.classList.add('bg-yellow-400');
			fired_events.push(event.event_id);
		}
	}

	highlightCellDuringPlayback(target_event, ['bg-orange-400']);
	fired_events.push(target_event.event_id);
	current_firing_event = target_event.event_id;

	if (target_event.event_type === 'STEP') {
		const children = events.filter((e) => e.parent_event === target_event.event_id && Math.abs(e.event_time - target_event.event_time) <= EPSILON_FOR_PAUSED_ON_STEP);
		for (const child of children) {
			const child_cell = document.getElementById(`td_event_time-${child.event_id}`);
			if (child_cell) child_cell.classList.add('step-child-suppress-highlight');
		}
	}

	is_highlight_initialized = true;
}

export function resetEventHighlighting() {
	fired_events = [];
	current_firing_event = undefined;
	cached_cells_to_update = null;
	is_unhighlight_function_called = false;
	is_highlight_initialized = false;
	// TODO: check if colored_cues is needed

	fired_event_highlighting_time_markers = new Map();
	for (const key of event_highlighting_time_markers.keys()) {
		fired_event_highlighting_time_markers.set(key, false);
	}

	// TODO: check if cleanUpUpdatingCellDurationsDuringPlayback is required
}

export const handleUpdatingCellDurationsDuringPlayback = function () {
	const waveform_store = useWaveformStore();
	if (!cached_cells_to_update) {
		cached_cells_to_update = Array.from(document.querySelectorAll('.update_durations_during_playback'));
	}
	let current_time_in_seconds = waveform_store.current_audio_playback_time;

	for (const cell_to_update of cached_cells_to_update) {
		let current_progress = 0;
		let current_duration = 0;
		let event_is_dmx = cell_to_update.getAttribute('event_type');
		let event_timeline_time = Number(cell_to_update.getAttribute('timeline_event_time')).toFixed(1);
		let event_duration = Number(cell_to_update.getAttribute('event_duration'));
		let event_pulse = Number(cell_to_update.getAttribute('event_pulse'));
		let event_lift_time = Number(cell_to_update.getAttribute('lift_time'));
		let event_igniter_pre_fire = Number(cell_to_update.getAttribute('igniter_pre_fire_in_seconds'));
		let cell_type = cell_to_update.getAttribute('cell_type');
		if (cell_type == 'time_cell') {
			if (event_is_dmx !== true) {
				if (event_duration != undefined && event_duration <= 0) {
					current_progress = 0;
				} else if (current_time_in_seconds + event_timeline_time < current_time_in_seconds) {
					current_progress = 0;
				} else if (current_time_in_seconds == undefined || isNaN(current_time_in_seconds) == true || event_duration == undefined || isNaN(event_duration) == true) {
					current_progress = 0;
				} else {
					current_duration = current_time_in_seconds - event_timeline_time;
					current_progress = current_duration / event_duration;

					if (current_progress >= 1 || current_progress <= 0 || isNaN(current_progress)) {
						current_progress = 0;
					}
				}
				cell_to_update.style.width = `${current_progress * 100}%`;
			} else {
				if (event_pulse != undefined && event_pulse <= 0) {
					current_progress = 0;
				} else if (current_time_in_seconds + event_timeline_time < current_time_in_seconds) {
					current_progress = 0;
				} else if (current_time_in_seconds == undefined || isNaN(current_time_in_seconds) == true || event_pulse == undefined || isNaN(event_pulse) == true) {
					current_progress = 0;
				} else {
					const event_duration_starting_time = Number(event_timeline_time).toFixed(1);
					current_duration = current_time_in_seconds - event_duration_starting_time;
					current_progress = current_duration / event_pulse;
					if (current_progress >= 1 || current_progress <= 0 || isNaN(current_progress)) {
						current_progress = 0;
					}
				}
			}
			cell_to_update.style.width = `${current_progress * 100}%`;
		} else if (cell_type == 'pre_fire') {
			if (event_igniter_pre_fire <= 0) {
				current_progress = 0;
			} else if (current_time_in_seconds + event_igniter_pre_fire < current_time_in_seconds) {
				current_progress = 0;
			} else if (current_time_in_seconds == undefined || isNaN(current_time_in_seconds) == true || event_igniter_pre_fire == undefined || isNaN(event_igniter_pre_fire) == true) {
				current_progress = 0;
			} else {
				const event_duration_starting_time = (event_timeline_time - event_igniter_pre_fire - event_lift_time ?? 0).toFixed(1);
				let current_duration = current_time_in_seconds - event_duration_starting_time;
				current_progress = current_duration / event_igniter_pre_fire;

				if (current_progress >= 1 || current_progress <= 0 || isNaN(current_progress)) {
					current_progress = 0;
				}
			}
			cell_to_update.style.background = `linear-gradient(to right, rgba(37, 99, 235, 0.2) ${current_progress * 100}%, transparent  ${current_progress * 100}%)`;
		} else if (cell_type == 'lift_time') {
			if (event_lift_time <= 0) {
				current_progress = 0;
			} else if (current_time_in_seconds + event_lift_time < current_time_in_seconds) {
				current_progress = 0;
			} else if (current_time_in_seconds == undefined || isNaN(current_time_in_seconds) == true || event_lift_time == undefined || isNaN(event_lift_time) == true) {
				current_progress = 0;
			} else {
				let event_duration_starting_time = (event_timeline_time - event_lift_time).toFixed(1);
				let current_duration = current_time_in_seconds - event_duration_starting_time;
				current_progress = current_duration / event_lift_time;
				if (current_progress >= 1 || current_progress <= 0 || isNaN(current_progress)) {
					current_progress = 0;
				}
			}
			cell_to_update.style.background = `linear-gradient(to right, rgba(37, 99, 235, 0.2) ${current_progress * 100}%, transparent  ${current_progress * 100}%)`;
		}
	}

	if (waveform_store.is_playing) {
		requestAnimationFrame(handleUpdatingCellDurationsDuringPlayback);
	}
};

function getClosestTimeMarker(map, current_time) {
	const keys = sorted_marker_keys;
	let lo = 0,
		hi = keys.length - 1,
		result = -1;
	while (lo <= hi) {
		const mid = (lo + hi) >>> 1;
		if (keys[mid] <= current_time) {
			result = mid;
			lo = mid + 1;
		} else {
			hi = mid - 1;
		}
	}
	return result >= 0 ? map.get(keys[result]) : undefined;
}
